import { all, call, put, takeLatest, select } from "redux-saga/effects"
import API from "../../helpers/sagaApi/sagaApi"
import Actions, { SagaAuthReduxTypes } from "./sagaAuthRedux"
import reducers from "../reducers"
import AppConfig from "../../configs/AppConfig"
import { currentLanguageRfc3066Fnc } from "../languageSwitcher/languageSwitcherSaga"

export function* getJwtToken() {
  const token = yield select((state: typeof reducers) => {
    return state.SagaAuth.token
  })
  return token
}

/**
 * Validate the jwt token by trying to retrieve the user's profile
 * @returns {IterableIterator<any>}
 */
export function* validateJwtToken(jwtToken: string) {
  const userProfile = yield requestUserProfile(jwtToken)
  // console.log("validateJwtToken", userProfile);
  if (userProfile) {
    return true
  } else {
    console.warn(
      "[validateJwtToken] Token validation failed, logging out the user.."
    )
    yield put(Actions.requestLogout())
    return false
  }
}

export function* requestLogin(action) {
  const tag = "[REQUEST_LOGIN]"
  try {
    // step 1: perform input validation
    const inputValid = action.email && action.password // IDE-Help, do not delete me
    if (inputValid) {
      // step 2 : validation passed..
      const response = yield call(
        API.requestLogin,
        action.email,
        action.password
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        const token = response.data.result
        const loginValid = yield validateJwtToken(token)
        if (loginValid) {
          if (AppConfig.isDevelopment) {
            console.log(`${tag} requestLogin response :`, response.data)
          }
          yield put(Actions.loginSuccess(response.data.result))
          window.location.reload()
        } else {
          const userProfileError = yield select(
            (s: any) => s.SagaAuth.userProfileError
          )
          throw new Error(userProfileError)
        }
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("Invalid data.") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestLogin failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.loginFailure(message))
  }
}

export function* requestUserProfile(jwtToken?: string) {
  const tag = "[REQUEST_USER_PROFILE]"
  try {
    // step 1: perform input validation
    const token = jwtToken ? jwtToken : yield getJwtToken() // IDE-Help, do not delete me
    if (token) {
      // step 2 : validation passed..
      const response = yield call(API.requestUserProfile, token)
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(`${tag} requestUserProfile response :`, response.data)
        }
        yield put(Actions.userProfileSuccess(response.data.result))
        return response.data.result
      } else {
        throw new Error(response.data.error)
      }
    } else {
      // throw new Error('xx.yyInvalidInput'); // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestUserProfile failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.userProfileFailure(message))
  }
}

export function* requestExternalLogin(action) {
  const tag = "[REQUEST_EXTERNAL_LOGIN]"
  try {
    // step 1: perform input validation
    const inputValid = action.data // IDE-Help, do not delete me
    if (inputValid) {
      // step 2 : validation passed..
      const response = yield call(API.requestExternalLogin, action.data)
      if (response.status === 200 && response.data.statusCode === 200) {
        const token = response.data.result
        const loginValid = yield validateJwtToken(token)
        if (loginValid) {
          if (AppConfig.isDevelopment) {
            console.log(`${tag} requestExternalLogin response :`, response.data)
          }
          yield put(Actions.externalLoginSuccess(response.data.result))
          window.location.reload()
        } else {
          const userProfileError = yield select(
            (s: any) => s.SagaAuth.userProfileError
          )
          throw new Error(userProfileError)
        }
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestExternalLogin failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.externalLoginFailure(message))
  }
}

export function* requestRegister(action) {
  const tag = "[REQUEST_REGISTER]"
  try {
    // step 1: perform input validation
    const inputValid = action.data // IDE-Help, do not delete me
    if (inputValid) {
      // step 2 : validation passed..
      const response = yield call(API.requestRegister, action.data)
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(`${tag} requestRegister response :`, response.data)
        }
        yield put(Actions.registerSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestRegister failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.registerFailure(message))
  }
}

export function* requestConfirmEmail(action) {
  const tag = "[REQUEST_CONFIRM_EMAIL]"
  try {
    // step 1: perform input validation
    const inputValid = action.userId && action.token // IDE-Help, do not delete me
    if (inputValid) {
      // step 2 : validation passed..
      const response = yield call(
        API.requestConfirmEmail,
        action.userId,
        action.token
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(`${tag} requestConfirmEmail response :`, response.data)
        }
        yield put(Actions.confirmEmailSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestConfirmEmail failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.confirmEmailFailure(message))
  }
}

export function* requestUpdateUserProfile(action) {
  const tag = "[REQUEST_UPDATE_USER_PROFILE]"
  try {
    // step 1: perform input validation
    const jwtToken = yield getJwtToken()
    if (jwtToken) {
      // step 2 : validation passed..
      const response = yield call(
        API.requestUpdateUserProfile,
        jwtToken,
        action.userProfile
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(
            `${tag} requestUpdateUserProfile response :`,
            response.data
          )
        }
        yield put(Actions.updateUserProfileSuccess(response.data.result))
        yield requestUserProfile(jwtToken)
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestUpdateUserProfile failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.updateUserProfileFailure(message))
  }
}

export function* requestResetUserPassword(action) {
  const tag = "[REQUEST_RESET_USER_PASSWORD]"
  try {
    // step 1: perform input validation
    const inputValid = true // IDE-Help, do not delete me
    const culture = yield select(currentLanguageRfc3066Fnc)
    if (inputValid) {
      // step 2 : validation passed..
      const response = yield call(API.requestResetUserPassword, action.email, culture)
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(
            `${tag} requestResetUserPassword response :`,
            response.data
          )
        }
        yield put(Actions.resetUserPasswordSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    const message = e.message ? e.message : "Failed"
    yield put(Actions.resetUserPasswordFailure(message))
  }
}

export function* requestConfirmResetUserPassword(action) {
  const tag = "[REQUEST_CONFIRM_RESET_USER_PASSWORD]"
  try {
    // step 1: perform input validation
    if (true) {
      // step 2 : validation passed..
      const response = yield call(
        API.requestConfirmResetUserPassword,
        action.confirmData
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(
            `${tag} requestConfirmResetUserPassword response :`,
            response.data
          )
        }
        yield put(Actions.confirmResetUserPasswordSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(
      `${tag} requestConfirmResetUserPassword failed with error :`,
      e
    )
    const message = e.message ? e.message : "Failed"
    yield put(Actions.confirmResetUserPasswordFailure(message))
  }
}

export function* requestCustomerRegister(action) {
  const tag = "[REQUEST_CUSTOMER_REGISTER]"
  const culture = yield select(currentLanguageRfc3066Fnc)
  try {
    if (true) {
      const response = yield call(
        API.requestCustomerRegister,
        action.customerReq,
        culture,
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(
            `${tag} requestCustomerRegister response :`,
            response.data
          )
        }
        yield put(Actions.customerRegisterSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestCustomerRegister failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.customerRegisterFailure(message))
  }
}

export function* requestUpdateCustomerProfile(action) {
  const tag = "[REQUEST_UPDATE_CUSTOMER_PROFILE]"
  try {
    // step 1: perform input validation
    const jwtToken = yield getJwtToken()
    if (jwtToken) {
      // step 2 : validation passed..
      const response = yield call(
        API.requestUpdateCustomerProfile,
        jwtToken,
        action.customerProfileReq
      )
      if (response.status === 200 && response.data.statusCode === 200) {
        if (AppConfig.isDevelopment) {
          console.log(
            `${tag} requestUpdateCustomerProfile response :`,
            response.data
          )
        }
        yield put(Actions.updateCustomerProfileSuccess(response.data.result))
      } else {
        throw new Error(response.data.error)
      }
    } else {
      throw new Error("xx.yyInvalidInput") // <-- fix me
    }
  } catch (e) {
    console.warn(`${tag} requestUpdateCustomerProfile failed with error :`, e)
    const message = e.message ? e.message : "Failed"
    yield put(Actions.updateCustomerProfileFailure(message))
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(SagaAuthReduxTypes.REQUEST_LOGIN, requestLogin),
    takeLatest(SagaAuthReduxTypes.REQUEST_USER_PROFILE, requestUserProfile),
    takeLatest(SagaAuthReduxTypes.REQUEST_EXTERNAL_LOGIN, requestExternalLogin),
    takeLatest(SagaAuthReduxTypes.REQUEST_REGISTER, requestRegister),
    takeLatest(SagaAuthReduxTypes.REQUEST_CONFIRM_EMAIL, requestConfirmEmail),
    takeLatest(
      SagaAuthReduxTypes.REQUEST_UPDATE_USER_PROFILE,
      requestUpdateUserProfile
    ),
    takeLatest(
      SagaAuthReduxTypes.REQUEST_RESET_USER_PASSWORD,
      requestResetUserPassword
    ),
    takeLatest(
      SagaAuthReduxTypes.REQUEST_CONFIRM_RESET_USER_PASSWORD,
      requestConfirmResetUserPassword
    ),
    takeLatest(
      SagaAuthReduxTypes.REQUEST_CUSTOMER_REGISTER,
      requestCustomerRegister
    ),
    takeLatest(
      SagaAuthReduxTypes.REQUEST_UPDATE_CUSTOMER_PROFILE,
      requestUpdateCustomerProfile
    )
  ])
}



// WEBPACK FOOTER //
// ./src/redux/sagaAuth/sagaAuthSaga.ts